'use client';

import { useState, useMemo, useId } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { LuHeart } from 'react-icons/lu';

import {
  buildCoursesQuery,
  fetchCourses,
  type CourseCategory,
} from '@eliadmin/core/src/api/courses/coursers-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { Button } from '../ui/button';
import { CourseCard } from './course-card';
import { CoursesSkeleton } from './courses-skeleton';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { canUseCourseListSeed, COURSES_PER_PAGE, FREE_COURSE_ID, type CourseListSeed } from './public-course-list';

const NO_LECTURER_SELECTED = 'none';
const ALL_CATEGORIES = 'all';
const FAVORITES_TAB = 'favorites';
const SKELETON_COURSE_COUNT = 3;

export interface CoursesListProps {
  locale: string;
  courseTypeId: number;
  allCategories: CourseCategory[];
  initialCourses?: CourseListSeed;
}

export function CoursesList({ locale, allCategories, courseTypeId, initialCourses }: CoursesListProps) {
  const [activeCategoryId, setActiveCategoryId] = useState<string>(ALL_CATEGORIES);
  const [selectedLecturer, setSelectedLecturer] = useState<string>(NO_LECTURER_SELECTED);
  // Every category filters the same mounted panel, including inactive tabs.
  const coursesPanelId = useId();

  const { t } = useTranslations();
  const me = useCustomerStore(state => state.me);

  // Filter out hidden categories
  const visibleCategories = useMemo(
    () => allCategories.filter(c => c.description !== '<p>hide</p>'),
    [allCategories],
  );

  const showFavorites = activeCategoryId === FAVORITES_TAB;

  // 🔥 Override courseTypeId dynamically if category 9 is active
  const effectiveCourseTypeId = Number(activeCategoryId) === 9 ? 4 : courseTypeId;
  const useSeed = canUseCourseListSeed(initialCourses, locale, effectiveCourseTypeId,
    me?.authToken, activeCategoryId, selectedLecturer);

  const coursesQueryParams = useMemo(
    () =>
      buildCoursesQuery({
        course_type_id: [effectiveCourseTypeId],
        categories:
          !showFavorites && activeCategoryId !== ALL_CATEGORIES ? [activeCategoryId] : undefined,
        lecturers:
          !showFavorites && selectedLecturer !== NO_LECTURER_SELECTED
            ? [Number(selectedLecturer)]
            : undefined,
      }),
    [activeCategoryId, selectedLecturer, effectiveCourseTypeId, showFavorites],
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    initialData: useSeed ? { pages: [initialCourses!.courses], pageParams: [1] } : undefined,
    initialDataUpdatedAt: initialCourses?.updatedAt,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: useSeed ? false : undefined,
    queryKey: [
      CacheKey.COURSES,
      locale,
      effectiveCourseTypeId,
      activeCategoryId,
      selectedLecturer,
      showFavorites,
      me?.authToken,
    ],
    queryFn: async ({ pageParam = 1 }) =>
      fetchCourses({
        locale,
        authToken: me?.authToken ?? '',
        query: {
          ...coursesQueryParams,
          page: pageParam,
          results: COURSES_PER_PAGE,
          favorite: showFavorites || undefined,
          order: 'desc',
          orderBy: 'created_at',
        },
      }),
    getNextPageParam: lastPage =>
      lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined,
    initialPageParam: 1,
  });

  // Only pin the free course to the top of the default, unfiltered view.
  const showFreeCourseFirst =
    activeCategoryId === ALL_CATEGORIES && selectedLecturer === NO_LECTURER_SELECTED;

  const { data: freeCourseData } = useQuery({
    initialData: useSeed ? initialCourses!.freeCourse : undefined,
    initialDataUpdatedAt: initialCourses?.updatedAt,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: useSeed ? false : undefined,
    queryKey: [CacheKey.COURSES, 'free-course', FREE_COURSE_ID, locale, me?.authToken],
    queryFn: () =>
      fetchCourses({
        locale,
        authToken: me?.authToken ?? '',
        query: {
          where: { id: { equals: FREE_COURSE_ID } },
          page: 1,
          results: 1,
        },
      }),
    enabled: showFreeCourseFirst,
  });

  const courses = useMemo(() => {
    const allCourses = data?.pages.flatMap(page => page.data) ?? [];
    if (!showFreeCourseFirst) return allCourses;

    const freeCourse = freeCourseData?.data?.[0];
    if (!freeCourse) return allCourses;

    return [freeCourse, ...allCourses.filter(course => course.id !== FREE_COURSE_ID)];
  }, [data, freeCourseData, showFreeCourseFirst]);

  const handleCategoryChange = (value: string) => {
    setActiveCategoryId(value);
    if (value === FAVORITES_TAB) setSelectedLecturer(NO_LECTURER_SELECTED);
  };

  const relevantCategories = useMemo(
    () =>
      courseTypeId === 1
        ? visibleCategories.filter(c => c.description !== '<p>Workouts</p>')
        : visibleCategories.filter(c => c.description === '<p>Workouts</p>'),
    [visibleCategories, courseTypeId],
  );

  const lecturers = useMemo(() => {
    const allLecturers = courses.flatMap(c => c.lecturers ?? []);
    const uniqueLecturers = Array.from(new Map(allLecturers.map(l => [l.id, l])).values());
    return uniqueLecturers;
  }, [courses]);

  return (
    <section className={cn('custom-section', 'my-10 pr-0 sm:my-12 sm:pr-6 lg:my-14 xl:my-16')}>
      <Tabs
        value={activeCategoryId}
        onValueChange={handleCategoryChange}
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 sm:gap-8 lg:gap-12 xl:gap-16',
        )}
      >
        {/* Category Tabs + Lecturer Filter */}
        <div className="flex w-full flex-col items-start justify-start gap-4 md:flex-row md:items-start md:justify-between md:gap-8">
          <TabsList
            className={cn(
              'flex h-fit w-full items-start justify-start gap-2 overflow-x-auto !rounded-none bg-transparent p-0 md:flex-wrap max-w-[700px]',
              'hide-scroll-bar overflow-scroll',
            )}
          >
            <TabsTrigger
              value={ALL_CATEGORIES}
              aria-controls={coursesPanelId}
              className="ea-courses-list__trigger whitespace-nowrap px-3 py-2 text-xs font-bold transition-all sm:py-3 sm:text-sm lg:px-4 lg:py-[14px] xl:text-base"
            >
              {t('text1947')}
            </TabsTrigger>

            {relevantCategories.map(category => (
              <TabsTrigger
                key={category.id}
                value={String(category.id)}
                aria-controls={coursesPanelId}
                className="ea-courses-list__trigger whitespace-nowrap px-3 py-2 text-xs font-bold transition-all sm:py-3 sm:text-sm lg:px-4 lg:py-[14px] xl:text-base"
              >
                {category.name}
              </TabsTrigger>
            ))}

            {me && (
              <TabsTrigger
                value={FAVORITES_TAB}
                aria-controls={coursesPanelId}
                className="ea-courses-list__trigger flex items-center justify-center gap-2 whitespace-nowrap px-3 py-2 text-xs font-bold transition-all sm:py-3 sm:text-sm lg:px-4 lg:py-[14px] xl:text-base"
              >
                <LuHeart size={20} /> {t('text516')}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Lecturer Select */}
          <div className="w-full pr-6 sm:min-w-[230px] sm:max-w-[230px] sm:pr-0">
            <Select
              value={selectedLecturer}
              onValueChange={setSelectedLecturer}
              disabled={showFavorites}
            >
              <SelectTrigger
                aria-label={t('text2195')}
                className={cn(
                  'ea-courses-list__select-trigger h-[46px] rounded-none border border-black bg-white font-bold text-black sm:text-base lg:h-12 xl:h-[54px]',
                  showFavorites && 'cursor-not-allowed opacity-50',
                )}
              >
                <SelectValue placeholder={t('text2195')} className="text-xs lg:text-sm" />
              </SelectTrigger>
              <SelectContent className="max-h-[250px] overflow-y-auto rounded-none border border-black bg-white">
                <SelectItem
                  value={NO_LECTURER_SELECTED}
                  className="rounded-none text-sm font-medium text-black/70 transition-all hover:bg-neutral-50/50"
                >
                  {t('text2195')}
                </SelectItem>
                {lecturers.map(l => (
                  <SelectItem
                    key={l?.id}
                    value={String(l?.id)}
                    className="rounded-none text-sm font-medium text-black/70 transition-all hover:bg-neutral-50/50"
                  >
                    {l?.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Course Grid */}
        <TabsContent id={coursesPanelId} value={activeCategoryId} className="!mt-0 w-full">
        {isLoading ? (
          <CoursesSkeleton amount={SKELETON_COURSE_COUNT} />
        ) : courses.length > 0 ? (
          <div
            className={cn(
              'custom-container',
              'grid grid-cols-1 gap-x-6 gap-y-6 pr-6 sm:pr-0 md:grid-cols-2 md:gap-y-12 lg:gap-x-8 xl:grid-cols-3 xl:gap-y-16',
            )}
          >
            {courses.map(course => (
              <CourseCard
                key={course.slug}
                course={course}
                headingAs="h2"
                selectedLecturerId={Number(selectedLecturer)}
              />
            ))}
          </div>
        ) : (
          <p className="w-full pt-4 text-center font-heading text-base font-bold text-black sm:text-lg xl:text-xl">
            {t('text3121')}
          </p>
        )}
        </TabsContent>

        {/* Load More */}
        {hasNextPage && (
          <div className="flex w-full items-center justify-center">
            <Button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              variant="dark"
              className="w-full sm:w-fit"
            >
              {t('text338')}
            </Button>
          </div>
        )}
      </Tabs>
    </section>
  );
}
